import {
  IMPORT_MAX,
  importPayload,
  planImport,
  type ImportSupplier,
  type Supplier,
  t,
} from '@procumi/shared';
import { useMemo, useRef, useState } from 'react';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { legal } from '../lib/market';
import { Dialog } from './Dialog';

/**
 * Textul unui fișier CSV. Excelul românesc salvează adesea în Windows-1250, nu
 * în UTF-8: dacă UTF-8 nu se potrivește, citim așa, ca „Oțel” să nu iasă „O?el”.
 */
async function readCsv(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1250').decode(bytes);
  }
}

const STATUS: Record<ImportSupplier['status'], string> = {
  new: 'got',
  exists: 'off',
  invalid: 'wait',
};

/**
 * Importul agendei dintr-un tabel. Totul se citește în browser, ca omul să
 * vadă înainte ce intră; la server pleacă doar furnizorii noi.
 */
export function ImportSuppliersDialog({
  existing,
  suggestions,
  toast,
  onDone,
  onClose,
}: {
  existing: Supplier[];
  /** categoriile firmei sau cele propuse, pentru câmpul de rezervă */
  suggestions: string[];
  toast: Toast;
  onDone: () => void;
  onClose: () => void;
}) {
  const L = legal();
  const [text, setText] = useState('');
  const [fallback, setFallback] = useState('');
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const plan = useMemo(
    () =>
      text.trim() ? planImport(text, existing, fallback, { taxLabel: L.taxId, anafAddress: L.checkCui }) : [],
    [text, existing, fallback],
  );
  const fresh = plan.filter((s) => s.status === 'new');
  const exists = plan.filter((s) => s.status === 'exists').length;
  const invalid = plan.filter((s) => s.status === 'invalid').length;
  const noCategory = plan.some((s) => s.reason === 'lipsește categoria');
  const tooMany = fresh.length > IMPORT_MAX;

  const pick = async (f: File | undefined) => {
    if (!f) return;
    if (/\.xlsx?$/i.test(f.name)) {
      toast(t('Din Excel: selectează tabelul, copiază-l (Ctrl+C) și lipește-l aici. Sau salvează-l ca CSV.'));
      return;
    }
    setText(await readCsv(f));
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.importSuppliers(importPayload(plan));
      onDone();
      onClose();
      toast(
        `${r.created === 1 ? t('Un furnizor adăugat') : t('{0} furnizori adăugați', r.created)}` +
          (r.skipped ? t('; {0} existau deja', r.skipped) : '') +
          (r.noAddress ? t('; {0} fără adresă la ANAF, adaugă-i de mână', r.noAddress) : ''),
      );
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} dirty={text.trim() !== ''} wide>
      <div className="dlg-h">
        <div>
          <h2>{t('Importă furnizori')}</h2>
          <p className="hint">
            {t(
              'Din Excel sau Google Sheets: selectezi tabelul cu tot cu capul lui, îl copiezi (Ctrl+C) și îl lipești mai jos. Sau alegi un fișier CSV.',
            )}
          </p>
        </div>
      </div>
      <div className="dlg-b">
        <label className="f" htmlFor="imp-text">
          {t('Tabelul cu furnizori')}
          <textarea
            id="imp-text"
            className="inp imp-text"
            rows={7}
            value={text}
            spellCheck={false}
            placeholder={t(
              'Firma	Persoana	Email	Telefon	Categorie\nArmax Distribuție	Ion Pop	ion@armax.ro	0722 123 456	Armătură',
            )}
            onChange={(e) => setText(e.target.value)}
          />
          <span className="hint" style={{ margin: 0 }}>
            {t(
              'Recunoaștem coloanele Firma, Email, Persoana, Telefon, Categorie, {0} și Adresa. Obligatorii: firma, emailul, {1}-ul și adresa{2}. Mai multe rânduri cu aceeași firmă devin persoane de contact ale aceluiași furnizor.',
              L.taxId,
              L.taxId,
              L.checkCui ? t(' (dacă lipsește, o luăm de la ANAF după CUI)') : '',
            )}
          </span>
        </label>
        <div className="imp-row">
          <button className="btn" type="button" onClick={() => file.current?.click()}>
            {t('Alege un fișier CSV')}
          </button>
          <input
            ref={file}
            type="file"
            accept=".csv,.txt,.xlsx,.xls,text/csv,text/plain"
            hidden
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <label className="f imp-fallback" htmlFor="imp-cat">
            {t('Categoria pentru cei fără categorie')}
            <input
              id="imp-cat"
              className="inp"
              list="imp-cats"
              value={fallback}
              placeholder={t('ex. Armătură')}
              onChange={(e) => setFallback(e.target.value)}
            />
            <datalist id="imp-cats">
              {suggestions.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
        </div>

        {plan.length ? (
          <>
            <p className="imp-sum" role="status">
              <b>{fresh.length === 1 ? t('Un furnizor nou') : t('{0} furnizori noi', fresh.length)}</b>
              {exists ? t(' · {0} există deja', exists) : ''}
              {invalid ? t(' · {0} nu pot intra', invalid) : ''}
              {noCategory && !fallback.trim() ? t(' — alege o categorie pentru cei fără') : ''}
            </p>
            <div className="imp-table-wrap">
              <table className="imp-table">
                <thead>
                  <tr>
                    <th>{t('Firma')}</th>
                    <th>{t('Persoane de contact')}</th>
                    <th>{t('Categorii')}</th>
                    <th>{t('Stare')}</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((s, i) => (
                    <tr key={`${s.name}-${i}`} className={s.status === 'new' ? undefined : 'imp-skip'}>
                      <td>
                        <b>{s.name || '—'}</b>
                        <small>{t('rândul {0}{1}', s.lines.join(', '), s.cui ? ` · ${s.cui}` : '')}</small>
                      </td>
                      <td>
                        {s.contacts.length
                          ? s.contacts.map((c) => (
                              <span key={c.email} className="imp-contact">
                                {c.name ? `${c.name} · ` : ''}
                                {c.email}
                              </span>
                            ))
                          : '—'}
                      </td>
                      <td>{s.categories.join(', ') || '—'}</td>
                      <td>
                        <span className={`pill ${STATUS[s.status]}`}>
                          {s.status === 'new'
                            ? s.reason
                              ? t('intră, {0}', s.reason)
                              : t('intră')
                            : s.reason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
      <div className="dlg-f">
        <button className="btn" type="button" onClick={onClose}>
          {t('Renunță')}
        </button>
        <button
          className="btn primary"
          type="button"
          disabled={busy || !fresh.length || tooMany}
          title={tooMany ? t('Cel mult {0} furnizori dintr-o dată', IMPORT_MAX) : undefined}
          onClick={() => void save()}
        >
          {busy
            ? t('Se importă…')
            : fresh.length
              ? t('Importă {0}', fresh.length === 1 ? t('un furnizor') : t('{0} furnizori', fresh.length))
              : t('Importă')}
        </button>
      </div>
    </Dialog>
  );
}
